import { lazy, Suspense, useEffect, CSSProperties } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { noteApplicantGuid } from './utils/sessionReplay';
import { LayoutProvider } from './context/LayoutContext';
import Layout from './components/layout/Layout';
import Confirmation from './pages/applicant/Confirmation';
import QuestionnaireBuilder from './pages/recruiter/QuestionnaireBuilder';
import Onboarding from './pages/applicant/Onboarding';
import Interview from './pages/applicant/Interview';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NotFound from './pages/common/NotFound';
import ProtectedRoute from './protectedRoutes/ProtectedRoute';
import UnauthorizedPage from './pages/common/UnauthorizedPage';
import AudioInterview from './pages/applicant/AudioInterview';
import InterviewCompleted from './pages/applicant/InterviewCompleted';

import Home from './pages/recruiter/Home';
import ExpandedInterviewPage from './pages/recruiter/ExpandedInterviewPage';
import iframeAuth from './utils/iframeAuth';
import { isNewOnboardingPageEnabled, isNewInterviewPageEnabled, isJobseekerHeaderFooterEnabled } from './utils/featureFlags';
import LandingPage from '@/pages/applicant/v2/LandingPage';
import StatsigBootstrap from './components/StatsigBootstrap';
import JobseekerChrome from './components/layout/JobseekerChrome';

const BlurBenchmarkPage = lazy(() => import('./pages/benchmark/BlurBenchmarkPage'));

// VI-717: the chrome-less success / already-completed pages fill their canvas.
// With the jobseeker header/footer on, they grow to fill the <JobseekerChrome> body
// (`flex:1`) and center within it so the footer stays visible; with it off they keep
// the original full-viewport white canvas (`minHeight:100vh`).
const fullPageWhiteCanvasStyle: CSSProperties = {
  background: '#ffffff',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  ...(isJobseekerHeaderFooterEnabled() ? { flex: '1 0 auto' } : { minHeight: '100vh' }),
};

/**
 * Tell session replay which applicant the session is about, on every navigation.
 *
 * Init alone covers the framed screener, which is deep-linked straight to the
 * onboarding URL, but a recruiter arrives at an applicant by navigating — so
 * without this their sessions would carry no guid at all. noteApplicantGuid
 * keeps the FIRST one it sees, so repeated calls are free.
 */
function ReplayRouteBinding() {
  const { pathname } = useLocation();
  useEffect(() => {
    noteApplicantGuid(pathname);
  }, [pathname]);
  return null;
}

function OnboardingGate() {
  const { applicantGuid } = useParams();
  if (isNewOnboardingPageEnabled()) {
    return <Navigate to={`/v2/interview/onboarding/${applicantGuid}`} replace />;
  }
  return <Onboarding />;
}

function App() {
  useEffect(() => {
    // Initialize iframe authentication on app mount
    iframeAuth.initialize({
      tokenParamName: 'jtToken', // Use existing jtToken parameter
      storageKey: 'iframe_jwt_token',
      autoSetHeaders: true
    });
  }, []);
  return (
    <div className="App">
      <StatsigBootstrap>
        <LayoutProvider>
          <BrowserRouter>
            <ReplayRouteBinding />
            {/* VI-717: renders the jobseeker header/footer around routed content on
                jobseeker paths only (flag-gated). No-op pass-through for recruiter. */}
            <JobseekerChrome>
            <Routes>
            {/* Recruiter routes */}
            <Route path="/v1/recruiter" element={<Layout userType="recruiter" />}>
              <Route index element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              } />
              <Route path="questionnaire-builder/:questionnaireId?" element={
                <ProtectedRoute>
                  <QuestionnaireBuilder />
                </ProtectedRoute>} 
              />

              <Route 
                path="interview/:applicantGuid" 
                element={
                  <ProtectedRoute>
                    <ExpandedInterviewPage />
                  </ProtectedRoute>
                } 
              />
              <Route path="unauthorized" element={<UnauthorizedPage />} />
            </Route>
            
            {/* VI-834: when the redesigned recording pages are enabled, serve them
                chrome-less (no Layout/UIContainer) inside the same centering wrapper
                as the v2 LandingPage. The pages render the new Figma layout internally
                (gated by the same flag). Kept at the same paths so LandingPage's
                start-interview navigation is unchanged. */}
            {isNewInterviewPageEnabled() && (
              <>
                {/* Full-width — the pages render a full-bleed canvas and cap their
                    own inner content at 1440 (VI-834). No maxWidth wrapper here or
                    the grey canvas would be guttered by white on wide screens. */}
                <Route path="/v1/interview/questions" element={<Interview />} />
                <Route path="/v1/interview/questions/audio" element={<AudioInterview />} />
                {/* Success + already-completed: chrome-less, full-bleed white (no
                    UIContainer card/icon header) per Figma. */}
                <Route path="/v1/interview/success" element={
                  <div style={fullPageWhiteCanvasStyle}>
                    <Confirmation />
                  </div>
                } />
                <Route path="/v1/interview/already-completed" element={
                  <div style={fullPageWhiteCanvasStyle}>
                    <InterviewCompleted />
                  </div>
                } />
              </>
            )}

            {/* V1 Interview routes */}
            <Route path="/v1/interview" element={<Layout />}>
              {/* <Route path="questionnaire-builder/:questionnaireId" element={<QuestionnaireBuilder />} /> */}
              <Route path="onboarding/:applicantGuid" element={<OnboardingGate />} />
              {!isNewInterviewPageEnabled() && <Route path="questions" element={<Interview />} />}
              {!isNewInterviewPageEnabled() && <Route path="questions/audio" element={<AudioInterview />} />}
              {!isNewInterviewPageEnabled() && <Route path="success" element={<Confirmation />} />}
              {!isNewInterviewPageEnabled() && <Route path="already-completed" element={<InterviewCompleted />} />}
            </Route>

            {/* V2 Interview routes — no Layout wrapper (LandingPage owns its own layout) */}
            <Route path="/v2/interview/onboarding/:applicantGuid" element={
              <div style={{ maxWidth: '1408px', margin: '0 auto', width: '100%' }}>
                <LandingPage />
              </div>
            } />
            
            {/* Blur benchmark route (gated by env) */}
            {import.meta.env.VITE_ENABLE_BLUR_BENCHMARK === 'true' && (
              <Route path="/v1/blur-benchmark" element={
                <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Loading benchmark...</div>}>
                  <BlurBenchmarkPage />
                </Suspense>
              } />
            )}

            {/* Catch-all route */}
            <Route path="*" element={<NotFound/>} />
            </Routes>
            </JobseekerChrome>
          </BrowserRouter>
        </LayoutProvider>
      </StatsigBootstrap>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default App;
